module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('section', {
    staticClass: "chap",
    attrs: {
      "epub:type": "chapter",
      "id": "chap-001"
    }
  }, [_c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_19",
      "title": "19"
    }
  }, [_c('div', {
    staticClass: "chap_debut"
  }), _c('h1', {
    staticClass: "chap_n",
    attrs: {
      "epub:type": "title"
    }
  }, [_c('span', [_vm._v("Chapter One")])]), _c('h1', {
    staticClass: "chap_tit",
    attrs: {
      "epub:type": "title"
    }
  }, [_c('span', [_vm._v("Introduction")])]), _c('div', {
    staticClass: "dev"
  }, [_c('p', {
    staticClass: "txt_courant_ssalinea"
  }, [_vm._v("Despite being an inheritor of the grand Reformation tradition of the sixteenth century I have to admit that until recently I had never read the ninety-five theses of Martin Luther, nailed to the wooden door of Wittenberg’s Castle Church, let alone taken note of the order of the theses (Ferguson 1996: 131–132). Surprising to me was that the first thesis from the list, which would change the course of church history, reads as follows:")]), _c('div', {
    staticClass: "cita"
  }, [_c('p', {
    staticClass: "txt_courant_ssalinea"
  }, [_vm._v("i Dominus & Magister noster Iesus Christus, dicendo poenitentia agite &c, omnem vitam fidelium poenitentiam esse voluit.")]), _c('p', {
    staticClass: "bl"
  }), _c('p', {
    staticClass: "txt_courant_ssalinea last"
  }, [_vm._v("[1. When our Lord and Master, Jesus Christ, said ‘repent’, he meant that the entire life of believers should be one of repentance.]")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("The indulgence controversy that sparked Luther’s concern did not prompt him to abandon his doctrine of repentance but rather to pursue a theological conversation with members of the university at Wittenberg and provide correction to the excesses of the medieval period.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Controversies over the doctrine of repentance can be traced throughout all the major phases of church history, and our present age is no exception. Two recent key theological debates have divided the evangelical church in particular, expressed first in the ‘Lordship Salvation’ controversy of the 1980s and 1990s and now the emerging ‘Hyper Grace’ controversy since 2010 (see "), _c('span', {
    staticClass: "renv",
    attrs: {
      "href": "#/chap14#chap-014",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 14")]), _vm._v(" below). Repentance has been a core topic of debate in both controversies, and this is not surprising in the light of church history.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("In this work I am not focused on the arguments mounted by proponents on opposing sides of these recent or ancient debates. Instead, my goal is to return to the Bible to offer a comprehensive overview of the theological witness of Scripture concerning the theme of repentance. The Old Testament will be given the coverage it deserves since it contains over 76% of the canonical witness, with due attention to the New Testament near the end of the study. Patient study of the "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_20",
      "title": "20"
    }
  }, [_vm._v("Old and New Testaments brings to light the striking similarity in their expression of the theology of repentance. In a final chapter I will provide some reflection on the theological implications of the biblical theology of repentance.")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Before moving to the biblical witness I want to provide some insight into my approach to writing biblical theology in general and a biblical theology of repentance in particular.")]), _c('div', {
    staticClass: "niv1",
    attrs: {
      "id": "niv1-001"
    }
  }, [_c('h2', {
    staticClass: "int_niv1",
    attrs: {
      "epub:type": "title"
    }
  }, [_vm._v("Defining biblical theology"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN1",
      "id": "ap_NFIN1-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1")])])]), _c('p', {
    staticClass: "txt_courant_ssalinea"
  }, [_vm._v("While the modern discipline of biblical theology is often linked to the inaugural lecture of Johann Philipp Gabler in 1787 and to key developments preceding this lecture in both Pietism and rationalism, the practice of biblical-theological reflection is as old as the Bible and its formative communities. The Old Testament is filled with texts that exemplify an impulse towards summarizing theological truth, whether synopses of the character (Exod. 34:6–7) or activity (Deut. 26:5–9) of Yahweh, or summaries of the ethical message of the prophets (2 Kgs 17:13; Zech. 1:3–4; 7:7–10). Even whole books such as Deuteronomy and Chronicles re-present earlier biblical materials, at times summarizing and focusing on particular emphases within the earlier books.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("A similar impulse can be discerned within the New Testament with its short encapsulations of the character and activity of Christ (Phil. 2:6–11; 1 Tim. 3:16) or its summaries of the ethical message of the Law and Prophets (Matt. 7:12; 22:37–40; Rom. 13:8–10; Gal. 5:14). Of course, the New Testament is filled with allusions and citations from the Old Testament, showing its concern to rehearse the theology of the Old Testament found in specific texts. At times the New Testament will trace theological themes through the Old Testament in more concentrated ways, like faith in Hebrews 11 or sin in Romans 3:9–20.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("The kind of biblical-theological reflection represented in the present book continues this biblical tradition. I have defined it elsewhere in the following way:")]), _c('div', {
    staticClass: "cita"
  }, [_c('p', {
    staticClass: "txt_courant_ssalinea last"
  }, [_vm._v("Biblical theology is a theological discipline that reflects on the theological witness of the Bible in its own idiom with attention to "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_21",
      "title": "21"
    }
  }, [_vm._v("both its unity and diversity. In partnership with sound exegetical theology, understood as disciplined reading of the individual pericopae and books of the Bible that seeks after their theological messages to their historical audiences, biblical theology discerns macro level connections within the biblical witness without ignoring disconnections between these various texts and books. The emphasis in biblical theology is on the messages of whole books, canonical sections, entire testaments, and the Bible as a collection and the connections between individual texts and these larger literary-canonical units."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN2",
      "id": "ap_NFIN2-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2")])])])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Underlying my approach to biblical theology lie six basic theological convictions concerning the Scriptures (Boda 2012a: 127–135). First, the Scriptures are communicative in character, that is, God communicated effectively through these texts to their ancient audiences (Heb. 1:1–3; Acts 1:16). Thus we can expect to understand these texts even though we are fully aware of the finite and fallen character of our human condition and our need for the illumination of the Holy Spirit and disciplined study of the texts to grasp the message of the Scriptures (Boda 2011c).")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Secondly, the Scriptures are incarnational in character, that is, God communicates through human figures in human language and forms at particular times in history (Heb. 1:1; Acts 1:16; 2 Peter 1:20–21; 2 Tim. 3:15–16). This means then that disciplined study of the texts will entail careful attention to the ancient languages through which and the ancient history in which these texts were written. It will seek to hear the theological message of the text as it was intended for the ancient community of God first before reflecting on how this message relates to our communities of faith today.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Thirdly, the Scriptures come to us in ‘inscripturated’ form, that is, while the Bible contains much text that reflects an original oral form, like proclamations of prophets (2 Peter 1:20–21), the focus of biblical theology is on the written form, and it is this that is called ‘God-breathed’ and ‘holy’ in 2 Timothy 3:15–16 (my tr.; cf. Rom. 15:4). We are thus not focused on the study of pre-canonical forms of the Scriptures, that is, presumed oral or written sources for the biblical books.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Fourthly, the Scriptures are authoritative in their final canonical form, providing normative theological and ethical truth (e.g. Matt. 5:17–20; Acts 24:14; 2 Tim. 3:14–17). This means that the present "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_22",
      "title": "22"
    }
  }, [_vm._v("study is not merely descriptive in character. The description of the theology of repentance is by nature also prescriptive, making demands on those individuals and communities who embrace these Scriptures as canon.")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Fifthly, the Scriptures are cumulative and progressive in character, that is, the Bible assumes an overall eschatological perspective, a movement towards a climactic future sometimes referred to as ‘the last days’ and associated in Hebrews 1:1–3 with the era inaugurated by Jesus Christ. This eschatological perspective did not result in the elimination of earlier stages of inscripturated revelation, since the Old Testament witness fills the pages of the New Testament, providing the theological foundation for most of its message. The post-canonical early church also continued to embrace the Old Testament as their Scriptures, drawing their arguments from Old and New Testaments alike. Nevertheless, it is clear that the earlier cumulative witness of the Old Testament must be seen through the final and climactic level focused on Jesus Christ.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Finally, the Scriptures are cohesive in character. This cohesion is seen in the outer shape that comprises two testaments, each of which has its own subcollections and orders of literary units. The present book follows the ancient Hebrew canonical shape of the Law, Prophets and Writings, taking the lead from Christ’s description of the Old Testament canon (Luke 24:44; cf. 11:51)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN3",
      "id": "ap_NFIN3-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3")])]), _vm._v(" The New Testament order is that found in all major manuscripts. Cohesion is also seen in the inner core of each testament. The Old and New Testaments witness to a single true God, revealed as Yahweh in the Old Testament and the triune God in the New Testament. The triune God Yahweh is identified as creator of the universe and redeemer of humanity. The Old and New Testaments also witness to a common human community. Obviously the human community reflected in the Old Testament from creation onwards reflects the same human community in view in the New Testament (e.g. Gen. 1; John 1). So also the redemptive community that gathers around Yahweh in the Old Testament and the triune God in the New Testament are treated as a single community of the people of God, evident in the use of identical terminology for both communities (e.g. Exod. 19:5–6; 1 Peter 2:9)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN4",
      "id": "ap_NFIN4-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4")])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("This theological foundation shapes the following agenda for biblical theology. The present study will remain sensitive to the diversity within "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_23",
      "title": "23"
    }
  }, [_vm._v("the canon, that is, not trying to make everything sound the same, and so giving attention to the canonical units as discrete witnesses, whether biblical books or biblical sections (e.g. Law, Former Prophets, Latter Prophets, Writings, or Old Testament, New Testament). But it will also seek after unity in the midst of this diversity, and bring the various units into dialogue and ultimately provide some insight into the common witness of the canon. The present study treats the Old Testament as legitimate Christian witness, granting attention to the breadth and depth of Old Testament texts in order to hear the voice of the Old Testament fully. Ultimately this will be considered in the light of the New Testament witness and key climactic developments in the final phase of redemptive history and canonical witness. But the riches of the Old Testament as Christian Theology will be mined since it was the triune God whose revelation is preserved in the Old Testament.")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("As noted above our concern is to hear the theological message of the text as it was intended for the ancient community of God first before reflecting on how this message relates to our community of faith today. Recent scholarship has leveraged the shape of the canon to highlight the intention of the one(s) responsible for the Hebrew canon and the reception of the ones who first received this authoritative collection:")]), _c('div', {
    staticClass: "cita"
  }, [_c('p', {
    staticClass: "txt_courant_ssalinea last"
  }, [_vm._v("The canon had editors who redacted their text in order to provide a general orientation, keeping in view the main themes of the literature lest these be lost in the mass of detail, reflecting on the significance of previously written material (when possible) and providing transitions to important new developments. Such junctures would occur at the most natural places to mark orientation: introductions, transitions and conclusions. These ‘contextualising’ redactions would then serve as a pair of ‘hermeneutical spectacles’ with which to view the contents of scripture. These ‘spectacles’ provided a definitive vision of how the redactors understood the nature and function of their text and the role it was to have in the life of the community."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN5",
      "id": "ap_NFIN5-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5")])])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("The fact that the Torah ends with the community poised to enter the land (Deut. 1), hearing a speech that presages Israel’s expulsion from the land (Deut. 4; 30; cf. Lev. 26), identifies its canonical audience "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_24",
      "title": "24"
    }
  }, [_vm._v("as that generation who would return to the land from exile."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN6",
      "id": "ap_NFIN6-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6")])]), _vm._v(" This same canonical audience can be discerned in the other major canonical sections of the Old Testament (cf. 2 Kgs 25 in the Former Prophets; Isa. 40 – 66, Jer. 25 – 52, Ezek. 34 – 48 or Haggai–Malachi in the Latter Prophets; and Chronicles, Ezra–Nehemiah, Daniel, Esther or Lamentations in the Writings). For this canonical audience the theology of repentance was key, providing direction for the emergence of a community that had experienced the discipline of Yahweh and was seeking a new way forward.")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("A strikingly similar canonical audience can be discerned within the New Testament. The Gospels picture John the Baptist and Jesus as restoration figures announcing the arrival of the kingdom of God, inviting the canonical audience to embrace this restoration kingdom by responding to their message. Acts pictures a restoration community echoing this message of Jesus, beginning in Jerusalem and ultimately spreading to the entire world, again inviting the canonical audience to respond to this message. This same canonical audience, however, is reminded that while the restoration kingdom is breaking in through the life, death, resurrection and ascension of Christ, its full realization will not be reached until the second coming of Christ (e.g. Matt. 26– 27; 1 Thess. 5; Revelation). The New Testament thus ends in similar fashion to the Old Testament canonical units, with a community longing for restoration, the realization of the kingdom of God on earth.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("The present study focuses considerable attention on the shape of the biblical text and its message to its original canonical audiences. This will lay a foundation for a final chapter on the theological implications of this biblical theology for contemporary Christian individuals and communities.")])]), _c('div', {
    staticClass: "niv1",
    attrs: {
      "id": "niv1-002"
    }
  }, [_c('h2', {
    staticClass: "int_niv1",
    attrs: {
      "epub:type": "title"
    }
  }, [_vm._v("Defining our biblical-theological topic")]), _c('p', {
    staticClass: "txt_courant_ssalinea"
  }, [_vm._v("One of the greatest challenges in writing biblical theology lies in defining the topic. Biblical theologians have typically sought to express the message of the Bible in its own idiom rather than impose a structure based on the idiom of the modern reader of the text, especially as articulated by the "), _c('i', [_vm._v("topoi ")]), _vm._v("or vocabulary of systematic theology. Seeking to express this biblical idiom and distance themselves from systematic "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_25",
      "title": "25"
    }
  }, [_vm._v("works, biblical theologians often erred by equating studies on words in the original languages of the Bible with biblical-theological reflection. Of course, sensitivity to the original language and its distinct vocabulary is relevant to expressing the message of the Bible in its own idiom, but research on vocabulary is only part of the task of biblical theology. The focus needs to be on themes that dominate the literature of the Old Testament and are expressed through a variety of linguistic means, including words, collocations, images, forms and related concepts. For instance, while ‘covenant’ is often seen as a key biblical-theological theme in the Old Testament, its study cannot be limited to an analysis of the Hebrew word "), _c('i', [_vm._v("b"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ĕ")]), _vm._v("rît")]), _vm._v(". While such a study would entail careful attention to the use of this Hebrew word within various texts in the Old Testament, a thorough biblical-theological study would need to attend to the breadth of vocabulary and collocations associated with covenant, as well as the key familial and imperial imagery used throughout the Old Testament and the variety of covenantal forms found in biblical books (e.g. Kalluveettil 1982; Hahn 2009). Such a study is necessary to lay bare the underlying conceptual frameworks for this theme in the Old Testament.")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("This process of defining the theme during the research stage of a project is not linear, that is, one does not begin with a clearly defined topic that remains unchanged throughout one’s study. Rather, one usually begins with a certain definition of a topic drawing on a limited pool of vocabulary or texts and then begins the process of listening to the message of all the Scriptures related to that topic, slowly and carefully drawing in related vocabulary, images, forms and their related passages throughout the biblical witness. Such research demands sensitivity to the unique contributions of each witness, not imposing a conceptual framework from one book of the biblical canon onto another, even while discerning connections to one’s larger theme.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("A review of key words dominating the lexical stock of repentance in the Old and New Testaments, however, is in order as we begin this study (e.g. Marshall et al. 1996: 1007–1008). Within Hebrew the most common word associated with repentance is the verb "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v(" (qal; ‘turn’, ‘return’)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN7",
      "id": "ap_NFIN7-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("7")])]), _vm._v(" One can ‘turn’ ("), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v(") to God or righteousness,"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN8",
      "id": "ap_NFIN8-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("8")])]), _vm._v(" as "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_26",
      "title": "26"
    }
  }, [_vm._v("well as ‘turn away’ ("), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v(") from evil."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN9",
      "id": "ap_NFIN9-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("9")])]), _vm._v(" This root is employed in the hiphil to refer to turning away from evil"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN10",
      "id": "ap_NFIN10-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("10")])]), _vm._v(" as well as to the action of one to prompt repentance in another."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN11",
      "id": "ap_NFIN11-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("11")])]), _vm._v(" The noun "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûbâ")]), _vm._v(" refers to repentance in Isaiah 30:15."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN12",
      "id": "ap_NFIN12-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("12")])]), _vm._v(" The second most common root is "), _c('i', [_vm._v("sûr ")]), _vm._v("(Fretheim 2006: 39, n. 37), both in the qal"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN13",
      "id": "ap_NFIN13-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("13")])]), _vm._v(" and hiphil"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN14",
      "id": "ap_NFIN14-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("14")])]), _vm._v(" stems. It is employed for turning aside from (qal) or putting aside (hiphil) foreign gods or sinful behaviour."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN15",
      "id": "ap_NFIN15-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("15")])]), _vm._v(" A third root is the verb "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal), which is more commonly used for divine shifts."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN16",
      "id": "ap_NFIN16-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("16")])]), _vm._v(" On a few occasions, however, "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) is used for human change (Job 42:6; Jer. 8:6; 31:19).")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("There is some challenge in distinguishing between the meaning of the verbs "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v(" and "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) in the Old Testament. These two verbs are employed several times alongside one another with the same actor."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN17",
      "id": "ap_NFIN17-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("17")])]), _vm._v(" In a few cases the order of these verbs is "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) then "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v(". In Exodus 13:17 "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) appears to refer to a change of mind (‘The people might change their minds when they see war’) and "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb ")]), _vm._v("to the outworking of that shift in terms of action (‘return to Egypt’)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN18",
      "id": "ap_NFIN18-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("18")])]), _vm._v(" Although in Jeremiah 8:6 "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) is used for repentance from wickedness (‘No man repented of his wickedness, saying, “What have I done?”’) and "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb ")]), _vm._v("is used for apostasy (‘Everyone turned to his course’), it is possible that "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) refers to a change of disposition reflected in the verbal declaration ‘What have I done?’, while "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v(" refers to a change in behaviour (‘his course’). This may also be the case in Jeremiah 4:28, which also contains the order "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) followed by "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v(":")]), _c('div', {
    staticClass: "stroplg type1"
  }, [_c('p', {
    staticClass: "verslg"
  }, [_c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_27",
      "title": "27"
    }
  }, [_vm._v("Because I have spoken, I have purposed,")])]), _c('p', {
    staticClass: "verslg"
  }, [_vm._v("And I will not change My mind ["), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(", niphal],")]), _c('p', {
    staticClass: "verslg"
  }, [_vm._v("nor will I turn ["), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v("] from it.")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("But it is possible that "), _c('span', {
    staticClass: "mev2"
  }, [_c('i', [_vm._v("š")])]), _c('i', [_vm._v("ûb ")]), _vm._v("refers to an internal shift (turning away from God’s purpose).")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("The reverse order of these verbs ("), _c('span', {
    staticClass: "mev2"
  }, [_c('i', [_vm._v("š")])]), _c('i', [_vm._v("ûb ")]), _vm._v("then "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(", niphal) occurs in a few other passages. In Jeremiah 31:19 clearly "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) is an activity that occurs after "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb ")]), _vm._v("(‘For after I turned back [or ‘turned away’; "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v("], I repented ["), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(", niphal]’). It is possible that "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb ")]), _vm._v("here refers to a return from exile followed by ‘repentance’ ("), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" niphal), but more likely "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb ")]), _vm._v("here refers to apostasy (turning away from God) followed by "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) referring to repentance (cf. Jer. 8:4, 11:10 for "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb ")]), _vm._v("as apostasy). In this latter case both verbs indicate a shift in internal disposition. This is most certainly the case in Exodus 32:12 (‘Turn ["), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v("] from Your burning anger and change Your mind ["), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(", niphal]) about doing harm to Your people’). In the other instances where the order "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb ")]), _vm._v("then "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) occurs, no clear difference is discernible between the two types of change:")]), _c('div', {
    staticClass: "tableau",
    attrs: {
      "id": "tab-002"
    }
  }, [_c('div', {
    staticClass: "table-align"
  }, [_c('table', {
    staticStyle: {
      "border-collapse": "collapse",
      "border-spacing": "0px"
    }
  }, [_c('colgroup', {
    staticStyle: {
      "padding": "4px"
    }
  }, [_c('col', {
    staticStyle: {
      "width": "20%"
    }
  }), _c('col', {
    staticStyle: {
      "padding": "4px"
    }
  })]), _c('tbody', [_c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("Joel 2:14")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘Who knows whether He will not turn ["), _c('span', {
    staticClass: "mev2"
  }, [_c('i', [_vm._v("š")])]), _c('i', [_vm._v("ûb")]), _vm._v("] and relent "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(", niphal]")]), _vm._v(" "), _c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("And leave a blessing behind Him’")])])]), _c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("Jonah 3:9")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘Who knows, God may turn ["), _c('span', {
    staticClass: "mev2"
  }, [_c('i', [_vm._v("š")])]), _c('i', [_vm._v("ûb")]), _vm._v("] and relent ["), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(", niphal]")]), _c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("and withdraw His burning anger so that we will not perish.’")])])]), _c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("Psalm 90:13")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘Do return ["), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v("], O L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v("; how long will it be?")]), _vm._v(" "), _c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("And be sorry [nḥm, niphal] for Your servants.’")])])])])])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("In three cases at most "), _c('i', [_vm._v("n"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("m")]), _vm._v(" (niphal) is used in connection with human repentance (Job 42:6; Jer. 8:6; 31:19). In Job 42:6 the change is a shift in perspective (see "), _c('span', {
    staticClass: "renv",
    attrs: {
      "href": "#/chap8#chap-008",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 8")]), _vm._v(" below), but neither Jeremiah 8:6 nor 31:19 makes clear whether this is a change in perspective or behaviour.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Other verbs used to express repentance include "), _c('i', [_vm._v("pnh ")]), _vm._v("(‘turn’; Isa. 45:22; Jer. 2:27);"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN19",
      "id": "ap_NFIN19-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("19")])]), _vm._v(" "), _c('i', [_vm._v("sbb ")]), _vm._v("(‘turn back’; 1 Kgs 18:37); "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("śṭ")]), _vm._v("h")]), _vm._v(" (‘turn away’;"), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_28",
      "title": "28"
    }
  }, [_vm._v(" Prov. 4:15);"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN20",
      "id": "ap_NFIN20-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("20")])]), _vm._v(" "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("dl")]), _vm._v(" (‘cease’; Isa. 1:16); "), _c('i', [_vm._v("rûm ")]), _vm._v("(hiphil; ‘stop’; Ezek. 45:9); ‘"), _c('i', [_vm._v("zb")]), _vm._v(" (‘forsake’; Isa. 55:7; Prov. 9:6); "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("lk")]), _vm._v(" (hiphil; ‘cast away’; Ezek. 18:31; 20:7); "), _c('i', [_vm._v("r"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ḥ")]), _vm._v("q")]), _vm._v(" (hiphil; ‘remove [sin] far away’; Job 11:14; 22:23); "), _c('i', [_vm._v("prq ")]), _vm._v("(Aram. ‘break away’; Dan. 4:27 [Hebr. 4:24]); "), _c('i', [_vm._v("bdl ")]), _vm._v("(niphal; ‘separate oneself’; Ezra 6:21; 10:11; Neh. 10:28) and "), _c('i', [_vm._v("zkr ")]), _vm._v("(‘remember’; e.g. Ezek. 36:31; Ps. 78:35; Eccl. 12:1)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN21",
      "id": "ap_NFIN21-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("21")])])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Since this study will also include reflection on the New Testament, a quick overview of vocabulary for repentance in the New Testament is in order."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN22",
      "id": "ap_NFIN22-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("22")])]), _vm._v(" Key vocabulary for repentance includes "), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v(","), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN23",
      "id": "ap_NFIN23-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("23")])]), _vm._v(" "), _c('i', [_vm._v("metanoia")]), _vm._v(","), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN24",
      "id": "ap_NFIN24-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("24")])]), _vm._v(" "), _c('i', [_vm._v("strephomai")]), _vm._v(","), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN25",
      "id": "ap_NFIN25-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("25")])]), _vm._v(" "), _c('i', [_vm._v("epistreph"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v(","), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN26",
      "id": "ap_NFIN26-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("26")])]), _vm._v(" "), _c('i', [_vm._v("epistroph"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ē")])]), _vm._v(","), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN27",
      "id": "ap_NFIN27-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("27")])]), _vm._v(" and possibly "), _c('i', [_vm._v("metamelomai")]), _vm._v("."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN28",
      "id": "ap_NFIN28-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("28")])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("It is difficult at times to identify the precise distinction between these various terms. The two key verbs "), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v(" and "), _c('i', [_vm._v("epistreph"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v(" appear alongside each other in Acts 3:19, and the same two verbs appear with the most common noun "), _c('i', [_vm._v("metanoia ")]), _vm._v("in Acts 26:20."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN29",
      "id": "ap_NFIN29-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("29")])]), _vm._v(" While it is clear that "), _c('i', [_vm._v("metamelomai ")]), _vm._v("refers to an internal shift, there is a long tradition of defining "), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v(" and "), _c('i', [_vm._v("metanoia ")]), _vm._v("in terms of a change of mind"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN30",
      "id": "ap_NFIN30-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("30")])]), _vm._v(". However, although "), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v(" can refer to a change in inner disposition "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_29",
      "title": "29"
    }
  }, [_vm._v("(e.g. Acts 8:22: ‘repent of this wickedness of yours. . . the intention of your heart’), it is regularly connected to a change in external activity:")])]), _c('div', {
    staticClass: "tableau",
    attrs: {
      "id": "tab-003"
    }
  }, [_c('div', {
    staticClass: "table-align"
  }, [_c('table', {
    staticStyle: {
      "border-collapse": "collapse",
      "border-spacing": "0px"
    }
  }, [_c('colgroup', {
    staticStyle: {
      "padding": "4px"
    }
  }, [_c('col', {
    staticStyle: {
      "width": "20%"
    }
  }), _c('col', {
    staticStyle: {
      "padding": "4px"
    }
  })]), _c('tbody', [_c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("2 Cor. 12:21")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘I am afraid that when I come again my God may humiliate me before you, and I may mourn over many of those who have sinned in the past and not repented ["), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("] of the impurity, immorality and sensuality which they have practiced.’")])])]), _c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("Rev. 2:5")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘repent ["), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("] and do the deeds you did at first’")])])]), _c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("Rev. 2:21")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘to repent ["), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("] of her immorality’")])])]), _c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("Rev. 2:22")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘repent ["), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("] of her deeds’")])])]), _c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("Rev. 9:20")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘did not repent ["), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("] of the works of their hands’")])])]), _c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("Rev. 9:21")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘they did not repent ["), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("] of their murders nor of their sorceries nor of their immorality nor of their thefts’")])])]), _c('tr', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("Rev. 16:11")])]), _c('td', {
    staticStyle: {
      "padding": "4px",
      "vertical-align": "top"
    }
  }, [_c('p', {
    staticClass: "txt_courant_justify"
  }, [_vm._v("‘they did not repent of their deeds’")])])])])])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("At the same time "), _c('i', [_vm._v("strephomai ")]), _vm._v("is used in Acts 7:39 to refer to the apostasy of the Jewish ancestors who ‘in their hearts turned back ["), _c('i', [_vm._v("strephomai")]), _vm._v("] to Egypt’, clearly an internal shift. The verb "), _c('i', [_vm._v("epistreph"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v(" is often used for a relational return to the Lord (Luke 1:16; Acts 9:35; 11:21; 14:15; 15:19; 26:20; 2 Cor. 3:16; 1 Thess. 1:9; 1 Peter 2:25), and in the citation of Malachi in Luke 1:17 it refers to an internal shift (‘"), _c('span', {
    staticClass: "small"
  }, [_vm._v("TO TURN THE HEARTS OF THE FATHERS BACK TO THE CHILDREN")]), _vm._v("’). Thus it appears that these words are not as clearly distinguished from one another as some studies would indicate. The focus of our study of the New Testament will be on the broader passages in which this vocabulary is found as well as on images, forms and concepts related to the vocabulary."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN31",
      "id": "ap_NFIN31-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("31")])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("As noted already such analysis is a key starting point but is not sufficient for studying this topic because the theological theme of repentance is expressed in ways other than through vocabulary, such "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_30",
      "title": "30"
    }
  }, [_vm._v("as through images, forms and concepts. Additionally, the semantic range of the common Hebrew and Greek words extends beyond the theme of repentance, and thus these verbs are not always related to this theme. Thus, while Hebrew and Greek vocabulary is key at the outset of the research for this particular study, prompting a close analysis of texts such as Deuteronomy and the Prophets in the Old Testament, or the Synoptic Gospels and Acts in the New Testament, my study even of these books extended beyond these words to their larger literary contexts as well as the broader lexical, imagistic, formal and conceptual stock of repentance within these books and ultimately within the rest of the Old and New Testaments.")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("The focus of this present book is on the biblical-theological theme of repentance."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN32",
      "id": "ap_NFIN32-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("32")])]), _vm._v(" Two passages in the Bible, one from the Old Testament and the other from the New, stand out as initial windows into this theme.")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("First, Zechariah 1:1–6 arises from a voice near the end of the canonical prophetic witness that gives the greatest attention to the theme of repentance in the Old Testament. Zechariah 1:1–6 contains a summary of the message of the ‘former prophets’, utilizing the dominant verb "), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("š"), _c('i', [_vm._v("ûb ")])]), _vm._v("to describe the prophet’s message in the following way:"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN33",
      "id": "ap_NFIN33-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("33")])])]), _c('div', {
    staticClass: "cita"
  }, [_c('p', {
    staticClass: "txt_courant_ssalinea"
  }, [_vm._v("Thus says the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(" of hosts, ‘"), _c('i', [_vm._v("Return ")]), _vm._v("["), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v("] to me,’ declares the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(" of hosts, ‘that I may "), _c('i', [_vm._v("return")]), _vm._v(" ["), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v("] to you,’ says the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(" of hosts. . . . Thus says the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(" of hosts, ‘"), _c('i', [_vm._v("Return ")]), _vm._v("["), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v("] now from your evil ways and from your evil deeds.’")]), _c('p', {
    staticClass: "txt_courant_ssalinea"
  }, [_vm._v(". . .")]), _c('p', {
    staticClass: "txt_courant_ssalinea last"
  }, [_vm._v("Then they "), _c('i', [_vm._v("repented ")]), _vm._v("["), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb")]), _vm._v("] and said, ‘As the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(" of hosts purposed to do to us in accordance with our ways and our deeds, so He has dealt with us.’")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Secondly, Acts 26:16–20 appears near the end of the corpus, with the most concentrated focus on repentance in the New Testament (Luke-Acts). This passage showcases Paul’s climactic speech before Agrippa in which we hear not only Jesus’ commission of the apostle but Paul’s appropriation of this commission to both Jew and Gentile, employing the three most common words associated with repentance ("), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v(", "), _c('i', [_vm._v("metanoia")]), _vm._v(", "), _c('i', [_vm._v("epistreph"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("):")]), _c('div', {
    staticClass: "cita"
  }, [_c('p', {
    staticClass: "txt_courant_ssalinea last"
  }, [_c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_31",
      "title": "31"
    }
  }, [_vm._v("‘But get up and stand on your feet; for this purpose I have appeared to you, to appoint you a minister and a witness not only to the things which you have seen, but also to the things in which I will appear to you; rescuing you from the Jewish people and from the Gentiles, to whom I am sending you, to open their eyes so that they may "), _c('i', [_vm._v("turn ")]), _vm._v("["), _c('i', [_vm._v("epistreph"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("] from darkness to light and from the dominion of Satan to God, that they may receive forgiveness of sins and an inheritance among those who have been sanctified by faith in Me.’ So, King Agrippa, I did not prove disobedient to the heavenly vision, but kept declaring both to those of Damascus first, and also at Jerusalem and then throughout all the region of Judea, and even to the Gentiles, that they should "), _c('i', [_vm._v("repent ")]), _vm._v("["), _c('i', [_vm._v("metanoe"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("] and "), _c('i', [_vm._v("turn ")]), _vm._v("["), _c('i', [_vm._v("epistreph"), _c('span', {
    staticClass: "mev2"
  }, [_vm._v("ō")])]), _vm._v("] to God, performing deeds appropriate to "), _c('i', [_vm._v("repentance ")]), _vm._v("["), _c('i', [_vm._v("metanoia")]), _vm._v("].")])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Repentance in these two passages is presented as a (re)turn to God and away from that which is contrary to God. Repentance also involves a shift in behaviour. Therefore, repentance in this study refers foremost to a turn or return to faithful relationship with God from a former state of estrangement: ‘Return to Me’ (Zech. 1:3). This limits the investigation to the relationship between humans and their God. This human–divine relationship will have implications for other relationships, such as with other humans or aspects/members of creation, since the relationship with God is often estranged due to violation of his priorities for human relationships and for the creational order. The present study is focused on human participation in this relationship, thus not on God’s ‘repentance’, but it will consider God’s role in inviting and enabling human repentance. Zechariah 1:4, 6 and Acts 26:16–20 reveal, however, that there are other aspects to repentance. It will become clear that repentance involves several dimensions, the first of which is made clear in these two passages: the behavioural (actual change in lifestyle and patterns of living), the affective (full engagement of internal orientation, all one’s heart) and the verbal/ritual (oral declarations expressing penitential desire whether in prayer or speech, including admission of sin and culpability, declaration of divine justice; various rites including sacrifice, fasting, sackcloth or baptism).")]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Placing a return to faithful relationship at the core of our definition means that passages which speak of admission of sin or remorse over sin are relevant to the topic of repentance."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN34",
      "id": "ap_NFIN34-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("34")])]), _vm._v(" Of course, we will discover "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_32",
      "title": "32"
    }
  }, [_vm._v("that at times repentance can be insincere, without full engagement of affections or a lack of sustained shift in behaviour. In addition, repentance is not always allowed, as we will see in Numbers 14 and the book of Jeremiah.")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Any work of biblical theology entails highlighting one strand from a complicated tapestry of biblical witnesses. Repentance is part of a much larger conceptual framework in the Old Testament related to sin and its remedy and constitutes only one of several strategies for dealing with sin in the Old Testament (cf. Kra"), _c('span', {
    staticClass: "mev1"
  }, [_vm._v("š")]), _vm._v("ovec 1999; Biddle 2005; Boda 2009b). It is also related to much larger theological themes ranging from soteriology to eschatology, and although I have sought to provide some insight into the broader theological tapestry of the Bible, at times I have had to limit my study through careful and disciplined selection.")])]), _c('div', {
    staticClass: "niv1",
    attrs: {
      "id": "niv1-003"
    }
  }, [_c('h2', {
    staticClass: "int_niv1",
    attrs: {
      "epub:type": "title"
    }
  }, [_vm._v("Past research on the biblical theology of repentance")]), _c('p', {
    staticClass: "txt_courant_ssalinea"
  }, [_vm._v("Although evident in nearly every corpus of the Old and New Testaments, the biblical theme of repentance has received little sustained attention over the past half century of scholarly research within the biblical guild. Some studies have restricted their considerations to the lexical stock of repentance."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN35",
      "id": "ap_NFIN35-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("35")])]), _vm._v(" Others, while broadening their scope beyond vocabulary to encompass the theme, have limited their attention to a particular testament, corpus or genre (see below)."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN36",
      "id": "ap_NFIN36-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("36")])]), _vm._v(" A third group of investigations, the many works tackling the overall theology of the Old and/or New Testament, has given the theme of repentance little more than passing mention."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN37",
      "id": "ap_NFIN37-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("37")])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Most Old Testament research takes its lead from two key early studies on Hebrew vocabulary. Holladay’s (1958) analysis of the root "), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("ûb ")]), _vm._v("focuses particularly on the meaning of this word in covenantal contexts. This then gives particular attention to the use of this word and its contribution to the theme of repentance in the Deuteronomic "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_33",
      "title": "33"
    }
  }, [_vm._v("and prophetic literature of the Old Testament. Milgrom (1975; 1976) analyses the root ‘"), _c('i', [_c('span', {
    staticClass: "mev2"
  }, [_vm._v("š")]), _vm._v("m ")]), _vm._v("(guilt) and its function within priestly contexts, and through this offers insight into repentance within another key tradition of the Old Testament. Studies of repentance have largely been confined to the prophetic literature of the Old Testament, although some have reflected on its role in Job and Chronicles."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN38",
      "id": "ap_NFIN38-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("38")])]), _vm._v(" In recent years particular attention has been given to the role of repentance within the community of Jews living in the wake of the destruction of Jerusalem, especially as expressed through the genre of penitential prayer as showcased in passages such as Ezra 9, Nehemiah 1, 9, Daniel 9 and Psalm 106."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN39",
      "id": "ap_NFIN39-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("39")])])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("Repentance has been a key topic within New Testament research, even though treatments of this theme within the entire New Testament are largely restricted to the first half of the twentieth century,"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN40",
      "id": "ap_NFIN40-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("40")])]), _vm._v(" or to the theologically contentious debates within evangelicalism over soteriology."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN41",
      "id": "ap_NFIN41-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("41")])]), _vm._v(" Most works have focused attention on a more limited corpus within the New Testament."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN42",
      "id": "ap_NFIN42-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("42")])])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("One can discern two key recent streams within New Testament research in relation to the study of repentance. The first stream is more socio-psychologically oriented, focusing on the phenomenon of religious conversion. Exemplary of this stream is Gaventa (1986) who traces the concept of ‘conversion’ throughout Luke-Acts, the Pauline epistles, John and 1 Peter, focusing on the language and narratives of conversion in these books. She uses the typology for religious change, alternation, pendulum conversion and transformation to help trace the different levels of religious change found within the New "), _c('span', {
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_34",
      "title": "34"
    }
  }, [_vm._v("Testament. By attending to the broader topic of conversion Gaventa provides helpful insights into the theme of repentance, especially by focusing on a diverse group of texts throughout the New Testament witness."), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN43",
      "id": "ap_NFIN43-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("43")])]), _vm._v(" The other key New Testament stream is more historically oriented, focusing on the message of repentance in the context of Second Temple Judaism. Exemplary of this stream is N. T. Wright who emphasizes the eschatological dimension of the call to repentance in the Gospels. Thus for Wright (1996: 249), ‘“Repentance” in Jesus’ context, then, would have carried the connotations of “what Israel must do if YHWH is to restore her fortunes at last”. Jesus, in announcing the kingdom, was declaring that Israel’s fortunes were being restored.’"), _c('sup', {
    staticClass: "footnote-out"
  }, [_c('span', {
    staticClass: "apnf",
    attrs: {
      "href": "#/appen9#NFIN44",
      "id": "ap_NFIN44-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("44")])]), _vm._v(" This perspective is a helpful corrective to more pietistic approaches to the theme of repentance, even if we will soon see that one does not have to choose between the redemptive-historical/eschatological and the ethical.")])]), _c('p', {
    staticClass: "txt_courant_justif"
  }, [_vm._v("These streams of past reflection on the theology of repentance in the Old and New Testaments have prompted and enriched much of my reflection in this present book. As we set out on this journey through the canon I am aware of many debates over each passage discussed as well as the various perspectives on the overall shape of the theme of repentance in the Old and New Testaments. I cannot possibly interact with these many voices in detail but only in limited ways as we progress through the canon. What follows is one perspective on the biblical-theological theme of repentance and its implications for Christian theology and practice today.")])])])])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-2d79414a","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/chap1.vue
// module id = 78
// module chunks = 0